// askSmart-ui/src/pages/TOSP/portal/DelegatesScreen.tsx
import React, { useCallback, useEffect, useState } from "react";
import { Tenant, tenantLabel } from "../types";
import { fetchDelegates, inviteDelegate, revokeDelegate } from "./delegatesApi";

/** Name · Email · Assigned Tenancy · Last Login · Actions. */
const COLS = "1.2fr 1.6fr 1.6fr 1.2fr 110px";

interface DelegatesScreenProps {
  /** Every tenancy this account is the PRIMARY contact for. */
  tenancies: Tenant[];
  onBack: () => void;
}

/** One person, and the tenancies they've been given. */
interface DelegateRow {
  userId: string;
  name: string;
  email: string;
  tenantIds: string[];
  lastLogin: string;
}

/**
 * Who else may act for this tenant's businesses.
 *
 * Listed per PERSON rather than per tenancy, because that's the question being
 * asked — "who has access to my things" — and somebody running three shops
 * usually gives their bookkeeper more than one. The tenancies each holds are a
 * column, and changing them is one dialog rather than three visits.
 *
 * The primary contact's screen alone: a delegate has been lent access and
 * can't lend it on.
 */
export default function DelegatesScreen({ tenancies, onBack }: DelegatesScreenProps) {
  const [rows, setRows] = useState<DelegateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [assigning, setAssigning] = useState<DelegateRow | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<DelegateRow | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // One read per tenancy, then folded together by person — the grant is
      // stored on the tenancy, so the per-person view is assembled here rather
      // than being a second copy of the same facts on the server.
      const perTenancy = await Promise.all(
        tenancies.map(async (tenancy) => ({
          tenantId: tenancy.id,
          delegates: await fetchDelegates(tenancy.id),
        })),
      );

      const byUser = new Map<string, DelegateRow>();
      for (const { tenantId, delegates } of perTenancy) {
        for (const delegate of delegates) {
          const held = byUser.get(delegate.userId);
          if (held) {
            held.tenantIds.push(tenantId);
          } else {
            byUser.set(delegate.userId, {
              userId: delegate.userId,
              name: delegate.name,
              email: delegate.email,
              tenantIds: [tenantId],
              lastLogin: "",
            });
          }
        }
      }

      const assembled = [...byUser.values()].sort((a, b) =>
        (a.name || a.email).localeCompare(b.name || b.email),
      );

      // Last login is an Auth0-backed fact, so it's fetched separately and
      // merged in — a failure there costs the column, not the page.
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/approved_users`,
          { credentials: "include" },
        );
        if (res.ok) {
          const data = await res.json();
          const users: any[] = data.users || data || [];
          const seen = new Map(
            users.map((user) => [String(user.user_id || user.id), user]),
          );
          for (const row of assembled) {
            const user = seen.get(row.userId);
            if (user?.last_login) row.lastLogin = user.last_login;
            if (!row.name && user?.name) row.name = user.name;
          }
        }
      } catch {
        // Column stays blank.
      }

      setRows(assembled);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the delegates.");
    } finally {
      setLoading(false);
    }
  }, [tenancies]);

  useEffect(() => {
    load();
  }, [load]);

  const tenancyName = (tenantId: string) => {
    const found = tenancies.find((tenancy) => tenancy.id === tenantId);
    return found ? tenantLabel(found) : tenantId;
  };

  const remove = async (row: DelegateRow) => {
    setConfirmRemove(null);
    setError(null);
    setNotice(null);
    try {
      // From every tenancy they hold — the row says "this person", so removing
      // it has to mean the same thing.
      for (const tenantId of row.tenantIds) {
        await revokeDelegate(tenantId, row.userId);
      }
      setNotice(`${row.name || row.email} no longer has access.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't remove them.");
    }
  };

  return (
    <div className="tosp-portal tosp-portal-wide">
      <header className="tosp-portal-detail-header tosp-delegates-header">
        {/* Same shape as Sales and Expenses: the way back, then where you
            are. The help mark and the one action sit on the same line. */}
        <button type="button" className="tosp-portal-back" onClick={onBack}>
          ‹ Dashboard
        </button>
        <h1 className="tosp-portal-detail-title">Manage Delegates</h1>
        <button
          type="button"
          className="tosp-help-toggle"
          aria-label={helpOpen ? "Hide explanation" : "What is a delegate?"}
          aria-expanded={helpOpen}
          title="What is a delegate?"
          onClick={() => setHelpOpen((was) => !was)}
        >
          ?
        </button>
        <button
          type="button"
          className="tosp-portal-primary-btn"
          onClick={() => {
            setNotice(null);
            setError(null);
            setAdding(true);
          }}
        >
          Add Delegate
        </button>
      </header>

      {helpOpen && (
        <p className="tosp-portal-help">
          Add a team member to grant them access to view your dashboard and
          enter monthly sales.
        </p>
      )}

      {error && (
        <div className="tosp-portal-notice tosp-portal-notice-error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="tosp-portal-notice" role="status">
          {notice}
        </div>
      )}

      <div className="template-card">
        <div className="overview-header" style={{ gridTemplateColumns: COLS }}>
          <div>Name</div>
          <div>Email</div>
          <div>Assigned Tenancy</div>
          <div>Last Login</div>
          <div>Actions</div>
        </div>

        {loading ? (
          <div className="tosp-delegate-empty">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="tosp-delegate-empty">No delegates yet.</div>
        ) : (
          rows.map((row) => (
            <div
              key={row.userId}
              className="overview-row"
              style={{ gridTemplateColumns: COLS }}
            >
              <div title={row.name}>{row.name || "—"}</div>
              <div title={row.email}>{row.email}</div>
              <div>
                {row.tenantIds.map((tenantId) => (
                  <div key={tenantId}>{tenancyName(tenantId)}</div>
                ))}
              </div>
              <div>
                {row.lastLogin ? new Date(row.lastLogin).toLocaleString() : "—"}
              </div>
              <div className="tosp-delegate-actions">
                <button
                  type="button"
                  className="tosp-icon-btn tosp-icon-multi"
                  title="Assign multiple tenancies"
                  onClick={() => setAssigning(row)}
                >
                  ☑
                </button>
                <button
                  type="button"
                  className="tosp-icon-btn tosp-icon-delete"
                  title="Remove delegate"
                  onClick={() => setConfirmRemove(row)}
                >
                  🗑
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {adding && (
        <AddDelegateModal
          tenancies={tenancies}
          onClose={() => setAdding(false)}
          onDone={async (message) => {
            setAdding(false);
            setNotice(message);
            await load();
          }}
        />
      )}

      {assigning && (
        <AssignTenanciesModal
          delegate={assigning}
          tenancies={tenancies}
          onClose={() => setAssigning(null)}
          onDone={async (message) => {
            setAssigning(null);
            setNotice(message);
            await load();
          }}
        />
      )}

      {confirmRemove && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "440px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Remove Delegate</h2>
            </div>
            <div className="smrtdocs-modal-body">
              <p style={{ marginTop: 0 }}>
                {confirmRemove.name || confirmRemove.email} will lose access to{" "}
                {confirmRemove.tenantIds.length === 1
                  ? tenancyName(confirmRemove.tenantIds[0])
                  : `all ${confirmRemove.tenantIds.length} of your tenancies`}
                . Anything they've already reported stays as it is.
              </p>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button className="btn-cancel" onClick={() => setConfirmRemove(null)}>
                  Cancel
                </button>
                <button
                  className="btn-save tosp-delegate-confirm-remove"
                  onClick={() => remove(confirmRemove)}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Inviting somebody, and saying which businesses they may act for. */
function AddDelegateModal({
  tenancies,
  onClose,
  onDone,
}: {
  tenancies: Tenant[];
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [email, setEmail] = useState("");
  // One tenancy is the ordinary case, so it starts selected rather than making
  // the tenant tick a box to describe the only situation they're in.
  const [chosen, setChosen] = useState<string[]>(
    tenancies.length === 1 ? [tenancies[0].id] : [],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (tenantId: string) =>
    setChosen((held) =>
      held.includes(tenantId)
        ? held.filter((id) => id !== tenantId)
        : [...held, tenantId],
    );

  const submit = async () => {
    const address = email.trim();
    if (!address || chosen.length === 0 || busy) return;
    setBusy(true);
    setError(null);

    // All the chosen tenancies in one request. Looping produced one invitation
    // per tenancy, and registration spends exactly one — so the invitee arrived
    // holding a single tenancy with the others orphaned.
    const result = await inviteDelegate(chosen, address);
    if (result.status === "error") {
      setError(result.message);
      setBusy(false);
      return;
    }
    const invited = result.status === "invited";

    // The two outcomes are genuinely different: one is finished, the other is
    // waiting on somebody else to register before it takes effect.
    onDone(
      invited
        ? `Invitation sent to ${address}. They'll have access once they've registered and completed their profile.`
        : `${address} can now act for ${chosen.length === 1 ? "that tenancy" : `${chosen.length} tenancies`}.`,
    );
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: "480px" }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>Add Delegate</h2>
        </div>
        <div className="smrtdocs-modal-body">
          <label className="tosp-label" htmlFor="tosp-delegate-email">
            Email address
          </label>
          <input
            id="tosp-delegate-email"
            className="manage-input"
            type="email"
            value={email}
            autoFocus
            disabled={busy}
            style={{ width: "100%", marginBottom: "18px" }}
            onChange={(e) => setEmail(e.target.value)}
          />

          {tenancies.length > 1 && (
            <>
              <span className="tosp-label">Which businesses?</span>
              <div className="tosp-delegate-picker">
                {tenancies.map((tenancy) => (
                  <label key={tenancy.id} className="tosp-checkbox">
                    <input
                      type="checkbox"
                      checked={chosen.includes(tenancy.id)}
                      disabled={busy}
                      onChange={() => toggle(tenancy.id)}
                    />
                    <span>{tenantLabel(tenancy)}</span>
                  </label>
                ))}
              </div>
            </>
          )}

          {error && (
            <div className="tosp-portal-notice tosp-portal-notice-error" role="alert">
              {error}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
            <button className="btn-cancel" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn-save"
              disabled={!email.trim() || chosen.length === 0 || busy}
              onClick={submit}
            >
              {busy ? "Adding…" : "Add Delegate"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Changing which businesses an existing delegate may act for. */
function AssignTenanciesModal({
  delegate,
  tenancies,
  onClose,
  onDone,
}: {
  delegate: DelegateRow;
  tenancies: Tenant[];
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [chosen, setChosen] = useState<string[]>(delegate.tenantIds);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (tenantId: string) =>
    setChosen((held) =>
      held.includes(tenantId)
        ? held.filter((id) => id !== tenantId)
        : [...held, tenantId],
    );

  const apply = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // Only the difference is written. Re-adding a tenancy they already hold
      // would be refused as a duplicate, and re-revoking one they don't would
      // be a 404 — neither is a real change.
      const added = chosen.filter((id) => !delegate.tenantIds.includes(id));
      const dropped = delegate.tenantIds.filter((id) => !chosen.includes(id));

      // Guarded: an empty list is "nothing to add", but the endpoint reads it
      // as a request naming no tenancy and rejects it.
      if (added.length > 0) {
        const result = await inviteDelegate(added, delegate.email);
        if (result.status === "error") throw new Error(result.message);
      }
      for (const tenantId of dropped) {
        await revokeDelegate(tenantId, delegate.userId);
      }

      onDone(
        chosen.length === 0
          ? `${delegate.name || delegate.email} no longer has access.`
          : `${delegate.name || delegate.email} now has ${chosen.length} of your tenancies.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't change that.");
      setBusy(false);
    }
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: "480px" }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>
            Tenancies for {delegate.name || delegate.email}
          </h2>
        </div>
        <div className="smrtdocs-modal-body">
          <div className="tosp-delegate-picker">
            {tenancies.map((tenancy) => (
              <label key={tenancy.id} className="tosp-checkbox">
                <input
                  type="checkbox"
                  checked={chosen.includes(tenancy.id)}
                  disabled={busy}
                  onChange={() => toggle(tenancy.id)}
                />
                <span>{tenantLabel(tenancy)}</span>
              </label>
            ))}
          </div>

          {chosen.length === 0 && (
            <p className="tosp-portal-subtitle" style={{ marginTop: 12 }}>
              With none selected they lose access altogether.
            </p>
          )}

          {error && (
            <div className="tosp-portal-notice tosp-portal-notice-error" role="alert">
              {error}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
            <button className="btn-cancel" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button className="btn-save" disabled={busy} onClick={apply}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
